import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FieldExpression, NodeExpression, term, toString as luceneToString } from 'lucene';
import { of, switchMap } from 'rxjs';

import { API_SALESDOC_SERVER_URL } from '../../config';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { OrderBy } from '../../shared/utils/constant';
import { Document, FieldOfDocument } from '../models/document';
import { ConnectionService } from '../../shared/services/connection.service';

const GET_DOCUMENT_URL = `${ API_SALESDOC_SERVER_URL }/v1/documents`;
const GET_DOCUMENT_V2_URL = `${ API_SALESDOC_SERVER_URL }/v2/documents`;

@Injectable({ providedIn: 'root' })
export class DocumentService {
	constructor(
		private readonly http: HttpClient,
		private readonly connectionService: ConnectionService
	) {}

	getDocumentDetails(
		id: number,
		fields: readonly FieldOfDocument[]
	) {
		const url = new URL(`${ GET_DOCUMENT_V2_URL }/${ id }`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		return this.http.get<Document>(url.toString());
	}

	getInvoiceByNumberAndDate(invoiceNumber: string, invoiceDate: string) {
		const url = new URL(`${ GET_DOCUMENT_V2_URL }/invoice-id`);
		url.searchParams.append('invoiceReference', invoiceNumber);
		url.searchParams.append('invoiceDate', invoiceDate);
		return this.http.get<number>(url.toString());
	}

	getInvoiceDetailsByNumberAndDate(
		invoiceNumber: string,
		invoiceDate: string,
		fields: readonly FieldOfDocument[]
	) {
		return this.getInvoiceByNumberAndDate(invoiceNumber, invoiceDate).pipe(
			switchMap(id => id ? this.getDocumentDetails(id, fields) : of(<Document>{}))
		);
	}

	getDocuments(
		fields: readonly FieldOfDocument[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Document>[] | OrderBy<keyof Document>,
		onlyDistinct: boolean
	) {
		const url = new URL(GET_DOCUMENT_V2_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<Document>>(url.toString());
	}

	getDocumentsCount(filter: string) {
		const url = new URL(GET_DOCUMENT_V2_URL);
		const params = url.searchParams;
		params.append('fields', 'type');
		params.append('filter', filter);
		params.append('onlyCount', 'true');
		params.append('distinct', 'false');
		return this.http.get<Pick<PaginatedResult<Document>, 'count'>>(url.toString());
	}

	getDocumentMills() {
		const url = new URL(`${ GET_DOCUMENT_URL }:getMillForDocument`);
		return this.http.get<PaginatedResult<Document>>(url.toString());
	}

	getDownloadDocumentUrl(id: number, filename: string) {
		const url = new URL(`${ GET_DOCUMENT_URL }/${ id }/download/${ encodeURIComponent(filename) }.pdf`);
		const params = url.searchParams;
		params.append('access_token', this.connectionService.getDownloadAccessToken());
		params.append('download', 'true');
		return url.toString();
	}

	getViewDocumentUrl(id: number, filename: string) {
		const url = new URL(`${ GET_DOCUMENT_URL }/${ id }/download/${ encodeURIComponent(filename) }.pdf`);
		const params = url.searchParams;
		params.append('access_token', this.connectionService.getDownloadAccessToken());
		params.append('download', 'false');
		return url.toString();
	}

	createLuceneFilter(textSearch: string, type: string) {
		const search = `*${ term.escape(textSearch) }*`;
		const documentNameField: FieldExpression = {
			field: 'name',
			term: search,
			quoted: false,
		};
		const supplierOrderNumberField = this.buildDocumentNumberFilter(
			textSearch,
			type === 'IN' || type === 'DA' ? 'documentLines.supplierOrderReference' : 'supplierOrderReference',
			'documentLines.supplierOrderReferenceItem',
		);
		const nameSupplierOrderNumberNode: NodeExpression = {
			left: documentNameField,
			operator: 'OR',
			parenthesized: true,
			right: supplierOrderNumberField
		};

		const customerOrderNumberField = this.buildDocumentNumberFilter(
			textSearch,
			type === 'IN' || type === 'DA' ? 'documentLines.customerOrderReference' : 'customerOrderReference',
			'documentLines.customerOrderItemReference',
		);

		const batchNumberField: FieldExpression = {
			field: 'documentLines.batchNumber',
			term: search,
			quoted: false
		};

		const customerOrderNumberAndBatchNumberNode: NodeExpression = {
			left: customerOrderNumberField,
			operator: 'OR',
			parenthesized: true,
			right: batchNumberField
		};

		const nameSupplierCustomerOrderNumberBatchNumberNode: NodeExpression = {
			left: nameSupplierOrderNumberNode,
			operator: 'OR',
			parenthesized: true,
			right: customerOrderNumberAndBatchNumberNode
		};

		const documentTypeField: FieldExpression = {
			field: 'type',
			term: type,
			quoted: false
		};

		const allNode: NodeExpression = {
			left: nameSupplierCustomerOrderNumberBatchNumberNode,
			operator: 'AND',
			parenthesized: true,
			right: documentTypeField
		};

		return luceneToString(allNode);
	}

	buildDocumentNumberFilter(textSearch: string, documentField: string, itemField: string) {
		const defaultCondition: FieldExpression = {
			field: documentField,
			term: `*${ term.escape(textSearch) }*`,
			quoted: false
		};
		if (textSearch.includes('/')) {
			const documentNumber = textSearch.slice(0, textSearch.lastIndexOf('/'));
			const itemNumber = textSearch.slice(textSearch.lastIndexOf('/') + 1);
			const isNum = /^\d+$/.test(itemNumber);
			if (isNum && documentNumber.length > 0) {
				const documentNumberField: FieldExpression = {
					field: documentField,
					term: `*${ term.escape(documentNumber) }*`,
					quoted: false
				};
				const documentItemNumberField: FieldExpression = {
					field: itemField,
					term: itemNumber,
					quoted: itemNumber === ''
				};
				const documentNumberNode: NodeExpression = {
					left: documentNumberField,
					operator: 'AND',
					parenthesized: true,
					right: documentItemNumberField
				};
				const finalDocumentNode: NodeExpression = {
					left: documentNumberNode,
					operator: 'OR',
					parenthesized: true,
					right: defaultCondition
				};
				return finalDocumentNode;
			}
			return defaultCondition;
		}
		return defaultCondition;
	}
}
